import { useState, useRef, useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import axios from "axios";
import API from "@/lib/api";
import CountryCodeSelect from "@/components/CountryCodeSelect";


const DOMAINS = [
  {
    key: "coding",
    label: "Coding",
    icon: "⌨",
    desc: "Code corpora, programming challenges, debugging traces, unit tests, and annotated repositories across multiple languages.",
  },
  {
    key: "operations_data",
    label: "Operations Data",
    icon: "⚙",
    desc: "Indian enterprise documents, insurance forms, legal contracts, government records, invoice and supply-chain data, and structured business workflows — especially vernacular-language operational data from Tier 2/3 contexts.",
  },
  {
    key: "voice_data",
    label: "Voice Data",
    icon: "🎙",
    desc: "Speech recordings, transcriptions, accent-diverse audio, prosody annotations, and conversational speech in Indian and global languages.",
  },
  {
    key: "physical_ai",
    label: "Physical AI Data",
    icon: "🤖",
    desc: "Sensor streams, robotics trajectories, embodied-agent logs, environment observations, and real-world interaction data.",
  },
];

const FORM_DOMAINS = [
  { key: "coding", label: "Code / Software repositories" },
  { key: "operations_data", label: "Business documents (emails, tickets, wikis, SOPs)" },
  { key: "coding_and_operations", label: "Both code and business documents" },
  { key: "academic_stem", label: "Academic / STEM content" },
  { key: "other", label: "Other" },
];

const COUNTRIES = [
  "Afghanistan","Albania","Algeria","Andorra","Angola","Antigua and Barbuda","Argentina","Armenia","Australia",
  "Austria","Azerbaijan","Bahamas","Bahrain","Bangladesh","Barbados","Belarus","Belgium","Belize","Benin","Bhutan",
  "Bolivia","Bosnia and Herzegovina","Botswana","Brazil","Brunei","Bulgaria","Burkina Faso","Burundi","Cabo Verde",
  "Cambodia","Cameroon","Canada","Central African Republic","Chad","Chile","China","Colombia","Comoros",
  "Congo (Brazzaville)","Congo (Kinshasa)","Costa Rica","Croatia","Cuba","Cyprus","Czech Republic","Denmark",
  "Djibouti","Dominica","Dominican Republic","Ecuador","Egypt","El Salvador","Equatorial Guinea","Eritrea",
  "Estonia","Eswatini","Ethiopia","Fiji","Finland","France","Gabon","Gambia","Georgia","Germany","Ghana",
  "Greece","Grenada","Guatemala","Guinea","Guinea-Bissau","Guyana","Haiti","Honduras","Hungary","Iceland",
  "India","Indonesia","Iran","Iraq","Ireland","Israel","Italy","Jamaica","Japan","Jordan","Kazakhstan","Kenya",
  "Kiribati","Kuwait","Kyrgyzstan","Laos","Latvia","Lebanon","Lesotho","Liberia","Libya","Liechtenstein",
  "Lithuania","Luxembourg","Madagascar","Malawi","Malaysia","Maldives","Mali","Malta","Marshall Islands",
  "Mauritania","Mauritius","Mexico","Micronesia","Moldova","Monaco","Mongolia","Montenegro","Morocco",
  "Mozambique","Myanmar","Namibia","Nauru","Nepal","Netherlands","New Zealand","Nicaragua","Niger","Nigeria",
  "North Korea","North Macedonia","Norway","Oman","Pakistan","Palau","Panama","Papua New Guinea","Paraguay",
  "Peru","Philippines","Poland","Portugal","Qatar","Romania","Russia","Rwanda","Saint Kitts and Nevis",
  "Saint Lucia","Saint Vincent and the Grenadines","Samoa","San Marino","Sao Tome and Principe","Saudi Arabia",
  "Senegal","Serbia","Seychelles","Sierra Leone","Singapore","Slovakia","Slovenia","Solomon Islands","Somalia",
  "South Africa","South Korea","South Sudan","Spain","Sri Lanka","Sudan","Suriname","Sweden","Switzerland",
  "Syria","Taiwan","Tajikistan","Tanzania","Thailand","Timor-Leste","Togo","Tonga","Trinidad and Tobago",
  "Tunisia","Turkey","Turkmenistan","Tuvalu","Uganda","Ukraine","United Arab Emirates","United Kingdom",
  "United States","Uruguay","Uzbekistan","Vanuatu","Vatican City","Venezuela","Vietnam","Yemen","Zambia","Zimbabwe",
  "Other",
];

const VOLUMES = [
  { value: "<1GB", label: "< 1 GB" },
  { value: "1-10GB", label: "1 – 10 GB" },
  { value: "10-100GB", label: "10 – 100 GB" },
  { value: "100GB-1TB", label: "100 GB – 1 TB" },
  { value: ">1TB", label: "> 1 TB" },
];

const FORMATS = [
  { key: "text", label: "Text" },
  { key: "audio", label: "Audio" },
  { key: "video", label: "Video" },
  { key: "images", label: "Images" },
  { key: "code", label: "Code" },
  { key: "structured", label: "Structured / CSV" },
];

const fieldBase = {
  background: "rgba(0,0,0,0.4)",
  border: "1px solid var(--line)",
  borderRadius: "6px",
  padding: "10px 14px",
  color: "var(--paper)",
  fontFamily: "var(--sans)",
  fontSize: "0.9rem",
  width: "100%",
  boxSizing: "border-box",
  outline: "none",
};
const fieldErr = { ...fieldBase, borderColor: "#e05555" };
const lbl = {
  display: "block",
  fontSize: "0.8rem",
  color: "var(--muted)",
  marginBottom: "6px",
  fontFamily: "var(--sans)",
};
const errMsg = { color: "#e05555", fontSize: "0.75rem", marginTop: "4px" };

export default function VendorPage() {
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const firstRef = useRef(null);

  // focus removed — auto-focusing the form caused the page to scroll past the hero on load

  const [form, setForm] = useState({
    full_name: "",
    work_email: "",
    company: "",
    role: "",
    domains: [],
    data_volume: "",
    data_formats: [],
    description: "",
    mobile_country_code: "",
    mobile_number: "",
    country: "",
    notes: "",
  });

  const set = (k, v) => {
    setForm(f => ({ ...f, [k]: v }));
    setErrors(e => ({ ...e, [k]: "" }));
  };

  const toggleDomain = (key) => {
    setForm(f => ({
      ...f,
      domains: f.domains.includes(key)
        ? f.domains.filter(d => d !== key)
        : [...f.domains, key],
    }));
    setErrors(e => ({ ...e, domains: "" }));
  };

  const toggleFormat = (key) => {
    setForm(f => ({
      ...f,
      data_formats: f.data_formats.includes(key)
        ? f.data_formats.filter(d => d !== key)
        : [...f.data_formats, key],
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errs = {};
    if (!form.full_name) errs.full_name = "Required";
    if (!form.work_email) errs.work_email = "Required";
    if (!form.company) errs.company = "Required";
    if (!form.role) errs.role = "Required";
    if (!form.domains.length) errs.domains = "Select at least one domain";
    if (!form.description) errs.description = "Required";
    if (Object.keys(errs).length) { setErrors(errs); return; }
    setErrors({});
    setIsSubmitting(true);
    try {
      const payload = {
        ...form,
        notes: form.country
          ? `Country: ${form.country}${form.notes ? " | " + form.notes : ""}`
          : form.notes,
        source: "organic",
      };
      await axios.post(`${API}/vendors`, payload);
      setIsSubmitted(true);
    } catch {
      alert("Failed to submit. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="bg-[#0A0A0A]" style={{ paddingTop: "96px" }}>
      <Helmet>
        <title>Become a Vendor — Nalandadata.ai</title>
        <meta name="description" content="Partner with Nalandadata.ai as a data vendor. We source high-quality datasets across coding, operations, voice, and physical AI domains." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://nalandadata.ai/vendors" />
        <meta property="og:title" content="Become a Vendor — Nalandadata.ai" />
        <meta property="og:description" content="Partner with Nalandadata.ai as a data vendor. We source high-quality datasets across coding, operations, voice, and physical AI domains." />
        <meta property="og:image" content="https://nalandadata.ai/og-social.png" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Become a Vendor — Nalandadata.ai" />
        <meta name="twitter:description" content="Partner with Nalandadata.ai as a data vendor. We source high-quality datasets across coding, operations, voice, and physical AI domains." />
        <meta name="twitter:image" content="https://nalandadata.ai/og-social.png" />
      </Helmet>

      {/* Hero */}
      <section className="s-band" style={{ borderTop: "none" }}>
        <div className="s-wrap">
          <nav className="s-crumb" aria-label="Breadcrumb" style={{ marginBottom: "18px" }}>
            <Link to="/">Home</Link> / Vendors
          </nav>
          <p className="s-eyebrow">Data Vendor Program</p>
          <div className="s-sec-head">
            <h2>Supply the data that trains the next frontier.</h2>
            <p className="lead">
              We're building a curated pipeline of domain-specific datasets for AI training.
              If you hold high-quality data in any of these domains, we want to hear from you.
            </p>
          </div>
        </div>
      </section>

      {/* Domain cards */}
      <section className="s-band">
        <div className="s-wrap">
          <p className="s-eyebrow" style={{ marginBottom: "32px" }}>Domains we're sourcing</p>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 260px), 1fr))", gap: "20px" }}>
            {DOMAINS.map(d => (
              <div key={d.key} style={{ background: "var(--ink-2)", border: "1px solid var(--line)", borderRadius: "12px", padding: "28px 24px" }}>
                <div style={{ fontSize: "1.8rem", marginBottom: "12px" }}>{d.icon}</div>
                <h3 style={{ fontFamily: "var(--sans)", fontSize: "1.05rem", fontWeight: 700, color: "var(--paper)", marginBottom: "10px" }}>{d.label}</h3>
                <p style={{ color: "var(--muted)", fontSize: "0.87rem", lineHeight: 1.65 }}>{d.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why partner */}
      <section className="s-band alt">
        <div className="s-wrap">
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 280px), 1fr))", gap: "48px", alignItems: "center" }}>
            <div>
              <p className="s-eyebrow">Why partner with us</p>
              <h2 style={{ fontFamily: "var(--sans)", fontSize: "clamp(1.4rem, 3vw, 2rem)", fontWeight: 800, color: "var(--paper)", lineHeight: 1.25, marginBottom: "20px" }}>
                Your data, responsibly commercialised.
              </h2>
              <p style={{ color: "var(--muted)", lineHeight: 1.75, marginBottom: "16px", fontSize: "0.93rem" }}>
                We work with data owners — individuals, institutions, and enterprises — to structure, license, and distribute domain-specific datasets to AI labs and research teams globally.
              </p>
              <p style={{ color: "var(--muted)", lineHeight: 1.75, fontSize: "0.93rem" }}>
                You retain ownership. We handle curation, quality verification, licensing negotiations, and distribution — sharing revenue transparently.
              </p>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
              {[
                { title: "Revenue share", body: "We share licensing revenue with you on every deal closed — no hidden deductions." },
                { title: "Quality partnership", body: "We co-invest in annotation, cleaning, and structure to maximise dataset value." },
                { title: "IP protection", body: "Contracts that protect your rights. You decide who gets access and under what terms." },
                { title: "Fast pipeline", body: "Dedicated partner onboarding — from agreement to first distribution in weeks." },
              ].map(item => (
                <div key={item.title} style={{ display: "flex", gap: "14px", alignItems: "flex-start" }}>
                  <span style={{ width: "8px", height: "8px", borderRadius: "50%", background: "var(--accent)", flexShrink: 0, marginTop: "6px" }} />
                  <div>
                    <p style={{ fontWeight: 600, color: "var(--paper)", fontSize: "0.93rem", marginBottom: "2px" }}>{item.title}</p>
                    <p style={{ color: "var(--muted)", fontSize: "0.85rem", lineHeight: 1.55 }}>{item.body}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Application form */}
      <section className="s-band" id="apply">
        <div className="s-wrap">
          <p className="s-eyebrow" style={{ marginBottom: "8px" }}>Apply</p>
          <h2 style={{ fontFamily: "var(--sans)", fontSize: "clamp(1.4rem, 3vw, 2rem)", fontWeight: 800, color: "var(--paper)", marginBottom: "40px" }}>
            Tell us about your data.
          </h2>

          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 340px), 1fr))", gap: "64px", alignItems: "start" }}>
            {/* Info sidebar */}
            <div>
              <div style={{ background: "var(--ink-2)", border: "1px solid var(--line)", borderRadius: "10px", padding: "24px", marginBottom: "16px" }}>
                <p style={{ fontWeight: 600, color: "var(--paper)", marginBottom: "8px", fontSize: "0.95rem" }}>What happens next?</p>
                <ol style={{ color: "var(--muted)", fontSize: "0.85rem", lineHeight: 1.7, paddingLeft: "18px", margin: 0 }}>
                  <li>We review your application within 48 hours</li>
                  <li>Our team schedules a discovery call</li>
                  <li>We assess dataset quality and fit</li>
                  <li>NDA signed — then licensing agreement</li>
                  <li>Distribution to verified buyers once agreement is in place</li>
                </ol>
              </div>
              <div style={{ background: "var(--ink-2)", border: "1px solid var(--line)", borderRadius: "10px", padding: "20px", marginBottom: "16px" }}>
                <p style={{ fontWeight: 600, color: "var(--paper)", marginBottom: "8px", fontSize: "0.95rem" }}>Data privacy & security</p>
                <p style={{ color: "var(--muted)", fontSize: "0.84rem", lineHeight: 1.7 }}>
                  An NDA is signed before any data transfer. Sensitive identifiers — names, phone numbers, Aadhaar, account details — are masked before any dataset is shown to buyers. Your raw data is never shared with a third party without your explicit approval of each deal.
                </p>
              </div>
              <div style={{ fontSize: "0.82rem", color: "var(--muted)", lineHeight: 1.6 }}>
                Questions? Email us at{" "}
                <a href="mailto:aanchal@nalandadata.ai" style={{ color: "var(--accent)", textDecoration: "none" }}>aanchal@nalandadata.ai</a>
              </div>
            </div>

            {/* Form */}
            <div style={{ background: "var(--ink-2)", border: "1px solid var(--line)", borderRadius: "12px", padding: "40px" }}>
              {isSubmitted ? (
                <div style={{ textAlign: "center", padding: "48px 0" }}>
                  <div style={{ width: "60px", height: "60px", borderRadius: "50%", background: "rgba(200,169,110,0.15)", border: "1px solid var(--accent)", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 24px", fontSize: "1.4rem", color: "var(--accent)" }}>✓</div>
                  <h3 style={{ fontFamily: "var(--sans)", fontSize: "1.4rem", fontWeight: 700, color: "var(--paper)", marginBottom: "12px" }}>Application Received</h3>
                  <p style={{ color: "var(--muted)", marginBottom: "28px", lineHeight: 1.7 }}>
                    Thank you for your interest in becoming a Nalandadata vendor. Our team will review your application and reach out within 48 hours.
                  </p>
                  <button onClick={() => setIsSubmitted(false)} className="s-btn ghost">Submit Another Application</button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: "22px" }}>

                  {/* Name + Email */}
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 200px), 1fr))", gap: "16px" }}>
                    <div>
                      <label style={lbl}>Full Name *</label>
                      <input ref={firstRef} value={form.full_name} onChange={e => set("full_name", e.target.value)} style={errors.full_name ? fieldErr : fieldBase} placeholder="Priya Sharma" />
                      {errors.full_name && <p style={errMsg}>{errors.full_name}</p>}
                    </div>
                    <div>
                      <label style={lbl}>Work Email *</label>
                      <input type="email" value={form.work_email} onChange={e => set("work_email", e.target.value)} style={errors.work_email ? fieldErr : fieldBase} placeholder="priya@company.com" />
                      {errors.work_email && <p style={errMsg}>{errors.work_email}</p>}
                    </div>
                  </div>

                  {/* Company + Role */}
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 200px), 1fr))", gap: "16px" }}>
                    <div>
                      <label style={lbl}>Company / Organisation *</label>
                      <input value={form.company} onChange={e => set("company", e.target.value)} style={errors.company ? fieldErr : fieldBase} placeholder="Organisation name" />
                      {errors.company && <p style={errMsg}>{errors.company}</p>}
                    </div>
                    <div>
                      <label style={lbl}>Your Role *</label>
                      <input value={form.role} onChange={e => set("role", e.target.value)} style={errors.role ? fieldErr : fieldBase} placeholder="CTO / Researcher / Data Lead" />
                      {errors.role && <p style={errMsg}>{errors.role}</p>}
                    </div>
                  </div>

                  {/* Mobile */}
                  <div>
                    <label style={lbl}>Mobile Number</label>
                    <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
                      <CountryCodeSelect
                        value={form.mobile_country_code}
                        onChange={v => set("mobile_country_code", v)}
                        required
                      />
                      <input type="tel" value={form.mobile_number} onChange={e => set("mobile_number", e.target.value)} style={{ ...fieldBase, flex: 1, minWidth: "140px" }} placeholder="Mobile number" />
                    </div>
                  </div>

                  {/* Country */}
                  <div>
                    <label style={lbl}>Country</label>
                    <div style={{ position: "relative" }}>
                      <select
                        value={form.country}
                        onChange={e => set("country", e.target.value)}
                        style={{ ...fieldBase, paddingRight: "32px", cursor: "pointer", appearance: "none", WebkitAppearance: "none" }}
                      >
                        <option value="">Select your country</option>
                        {COUNTRIES.map(c => (
                          <option key={c} value={c} style={{ background: "#111111", color: "#F0EBE0" }}>{c}</option>
                        ))}
                      </select>
                      <span style={{ position: "absolute", right: "10px", top: "50%", transform: "translateY(-50%)", color: "var(--muted)", pointerEvents: "none", fontSize: "12px" }}>▾</span>
                    </div>
                  </div>

                  {/* Domains */}
                  <div>
                    <label style={lbl}>Data Domain(s) *</label>
                    <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                      {FORM_DOMAINS.map(d => {
                        const active = form.domains.includes(d.key);
                        return (
                          <button
                            type="button"
                            key={d.key}
                            onClick={() => toggleDomain(d.key)}
                            style={{
                              background: active ? "rgba(200,169,110,0.12)" : "rgba(0,0,0,0.3)",
                              border: `1px solid ${active ? "var(--accent)" : errors.domains ? "#e05555" : "var(--line)"}`,
                              borderRadius: "8px",
                              padding: "10px 14px",
                              color: active ? "var(--accent)" : "var(--muted)",
                              fontFamily: "var(--sans)",
                              fontSize: "0.85rem",
                              fontWeight: active ? 600 : 400,
                              cursor: "pointer",
                              textAlign: "left",
                              transition: "all 0.15s",
                            }}
                          >
                            {d.label}
                          </button>
                        );
                      })}
                    </div>
                    {errors.domains && <p style={errMsg}>{errors.domains}</p>}
                  </div>

                  {/* Data formats */}
                  <div>
                    <label style={lbl}>Data Format(s)</label>
                    <div style={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
                      {FORMATS.map(f => {
                        const active = form.data_formats.includes(f.key);
                        return (
                          <button
                            type="button"
                            key={f.key}
                            onClick={() => toggleFormat(f.key)}
                            style={{
                              background: active ? "rgba(200,169,110,0.12)" : "rgba(0,0,0,0.3)",
                              border: `1px solid ${active ? "var(--accent)" : "var(--line)"}`,
                              borderRadius: "20px",
                              padding: "5px 14px",
                              color: active ? "var(--accent)" : "var(--muted)",
                              fontFamily: "var(--sans)",
                              fontSize: "0.82rem",
                              fontWeight: active ? 600 : 400,
                              cursor: "pointer",
                              transition: "all 0.15s",
                            }}
                          >
                            {f.label}
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  {/* Volume */}
                  <div>
                    <label style={lbl}>Approximate Data Volume</label>
                    <select value={form.data_volume} onChange={e => set("data_volume", e.target.value)} style={fieldBase}>
                      <option value="">Select volume</option>
                      {VOLUMES.map(v => <option key={v.value} value={v.value}>{v.label}</option>)}
                    </select>
                  </div>

                  {/* Description */}
                  <div>
                    <label style={lbl}>Describe your dataset *</label>
                    <textarea
                      value={form.description}
                      onChange={e => set("description", e.target.value)}
                      style={{ ...fieldBase, minHeight: "120px", resize: "vertical" }}
                      placeholder="What data do you have? How was it collected? What makes it unique or valuable for AI training?"
                    />
                    {errors.description && <p style={errMsg}>{errors.description}</p>}
                  </div>

                  {/* Notes */}
                  <div>
                    <label style={lbl}>Additional notes</label>
                    <textarea
                      value={form.notes}
                      onChange={e => set("notes", e.target.value)}
                      style={{ ...fieldBase, minHeight: "80px", resize: "vertical" }}
                      placeholder="Licensing preferences, existing partnerships, open-source status, any constraints..."
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="s-btn primary"
                    style={{ width: "100%", justifyContent: "center", opacity: isSubmitting ? 0.7 : 1, cursor: isSubmitting ? "not-allowed" : "pointer" }}
                  >
                    {isSubmitting ? "Submitting..." : "Submit Application →"}
                  </button>

                  <p style={{ fontSize: "0.75rem", color: "var(--muted)", textAlign: "center" }}>
                    By submitting, you agree to our <Link to="/privacy" style={{ color: "var(--accent)", textDecoration: "none" }}>privacy policy</Link>. We will never share your information without your consent.
                  </p>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
